import type {
  AssessmentNavigationAction,
  AssessmentNavigationAlignment,
  AssessmentNavigationButtonSize,
  AssessmentNavigationButtonVariant,
  AssessmentNavigationIconPosition,
  AssessmentNavigationOption,
} from "../types/blocks/assessment/AssessmentNavigationOption";

const ALIGNMENTS: AssessmentNavigationAlignment[] = ["between", "start", "center", "end"];
const SIZES: AssessmentNavigationButtonSize[] = ["sm", "default", "lg"];
const VARIANTS: AssessmentNavigationButtonVariant[] = [
  "default",
  "destructive",
  "outline",
  "secondary",
  "ghost",
  "link",
];
const ICON_POSITIONS: AssessmentNavigationIconPosition[] = ["left", "right"];

export const DEFAULT_ASSESSMENT_NAVIGATION: AssessmentNavigationOption = {
  alignment: "between",
  size: "default",
  back: {
    visible: true,
    enabled: true,
    label: "Back",
    icon: "lucide:arrow-left",
    iconPosition: "left",
    variant: "outline",
    validateForm: false,
    automationPath: "",
    navigationPath: "",
    navigationDelaySeconds: 0,
    preloadDestination: false,
  },
  next: {
    visible: true,
    enabled: true,
    label: "Next",
    icon: "lucide:arrow-right",
    iconPosition: "right",
    variant: "default",
    validateForm: true,
    automationPath: "",
    navigationPath: "",
    navigationDelaySeconds: 0,
    preloadDestination: false,
  },
};

const normalizeString = (value: unknown, fallback: string) =>
  typeof value === "string" ? value : fallback;

const normalizeBoolean = (value: unknown, fallback: boolean) =>
  typeof value === "boolean" ? value : fallback;

const normalizeDelay = (value: unknown, fallback: number) => {
  const numericValue = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0
    ? numericValue
    : fallback;
};

const normalizeEnum = <T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
) => allowed.includes(value as T) ? value as T : fallback;

const normalizeAction = (
  value: unknown,
  fallback: AssessmentNavigationAction,
): AssessmentNavigationAction => {
  const action = value && typeof value === "object"
    ? value as Partial<AssessmentNavigationAction>
    : {};

  return {
    visible: normalizeBoolean(action.visible, fallback.visible),
    enabled: normalizeBoolean(action.enabled, fallback.enabled),
    label: normalizeString(action.label, fallback.label),
    icon: normalizeString(action.icon, fallback.icon),
    iconPosition: normalizeEnum(action.iconPosition, ICON_POSITIONS, fallback.iconPosition),
    variant: normalizeEnum(action.variant, VARIANTS, fallback.variant),
    validateForm: normalizeBoolean(action.validateForm, fallback.validateForm),
    automationPath: normalizeString(action.automationPath, fallback.automationPath),
    navigationPath: normalizeString(action.navigationPath, fallback.navigationPath),
    navigationDelaySeconds: normalizeDelay(
      action.navigationDelaySeconds,
      fallback.navigationDelaySeconds,
    ),
    preloadDestination: normalizeBoolean(
      action.preloadDestination,
      fallback.preloadDestination,
    ),
  };
};

export const normalizeAssessmentNavigation = (
  value: unknown,
): AssessmentNavigationOption => {
  const option = value && typeof value === "object"
    ? value as Partial<AssessmentNavigationOption>
    : {};

  return {
    alignment: normalizeEnum(
      option.alignment,
      ALIGNMENTS,
      DEFAULT_ASSESSMENT_NAVIGATION.alignment,
    ),
    size: normalizeEnum(option.size, SIZES, DEFAULT_ASSESSMENT_NAVIGATION.size),
    back: normalizeAction(option.back, DEFAULT_ASSESSMENT_NAVIGATION.back),
    next: normalizeAction(option.next, DEFAULT_ASSESSMENT_NAVIGATION.next),
  };
};
